import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import {
  BusModule,
  PanelHeaderDivider,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { BackupVehicleInfo } from "features/CenterContent/VehicleDetails/BackupAlternativeTransportDetails/BackupVehicleInfo";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { useParams } from "react-router-dom";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { Module } from "shared/components/Module";
import { Text, VStack } from "shared/components/Spor";
import { EmptyContent } from "shared/components/states/EmptyContent";
import { ErrorContent } from "shared/components/states/ErrorContent";
import { LoadingContent } from "shared/components/states/LoadingContent";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import * as z from "zod";

const BackupAlternativeTransportDetails = () => {
  const { vehicleUuid } = useParams();

  const { status, data: backupVehicle } = useQuery({
    queryKey: ["backupDetails", "backup", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/backup/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  return (
    <>
      <TabsCenter isBackUpBus />
      <BusModule
        sectionState={{
          fetchStatus: status,
          isEmpty: !backupVehicle,
          emptyReason: "Fant ingen detaljer om bussen",
        }}
      >
        {backupVehicle && (
          <>
            <PanelHeaderDivider>
              <BackupVehicleInfo reserveAlternativeTransport={backupVehicle} />
            </PanelHeaderDivider>
            <PanelScrollContent>
              <PanelScrollArea>
                <PanelModuleGrid>
                  <VStack
                    style={{
                      width: "100%",
                      padding: 0,
                      alignItems: "stretch",
                    }}
                  >
                    {backupVehicle.notes.length > 0 && (
                      <div style={{ width: "100%", padding: "0.75rem" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {backupVehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    )}
                    <Module title="Logg">
                      <OperationalLog
                        vehicleId={backupVehicle.id}
                        logs={backupVehicle.logs ?? []}
                      />
                    </Module>
                    {/** Loading, Error, and Empty content examples for design check, remove before merge */}
                    <LoadingContent size="sm" />
                    <LoadingContent size="md" />
                    <LoadingContent size="lg" />
                    <ErrorContent size="sm" />
                    <ErrorContent size="md" />
                    <ErrorContent size="lg" />
                    <EmptyContent
                      reason="Her er det ingen data tilgjengelig, bare eksempeltekst"
                      size="sm"
                    />
                    <EmptyContent
                      reason="Her er det ingen data tilgjengelig, bare eksempeltekst"
                      size="md"
                    />
                    <EmptyContent
                      reason="Her er det ingen data tilgjengelig, bare eksempeltekst"
                      size="lg"
                    />
                  </VStack>
                </PanelModuleGrid>
              </PanelScrollArea>
            </PanelScrollContent>
          </>
        )}
      </BusModule>
    </>
  );
};

export default withErrorBoundary(BackupAlternativeTransportDetails);
